import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { LeaveGuard } from '../../../../shared/sso-workspace/leave-guard'
import { DockedSaveBar } from '../../../../shared/sso-workspace/save-bar'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { scrollToSection } from '../../../../shared/sso-workspace/section-nav'
import { formatAgo, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconAdd, IconAlertRoute, IconDelete } from '../../../../shared/icon/icon'
import { createRoute, deleteRoute, getRoute, listTargets, routeState, updateRoute } from './alert-routing-api'
import { TriggerStep, FiltersStep, WhenStep, ActionsStep, TestStep, pendingSecret, SEVERITY_FLOOR, digestEvery } from './editor-steps'
import { useAlertRoutingAccess } from './access'
import { routeOrgScoped, useCatalog } from './catalog'
import { categoryNoun } from './route-summary'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../shared/audit/history/audit-record-context'
import { ROUTES_PAGE, routeHref } from './paths'
import { editableRoute, emptyRoute, toBody } from './route-body'
import { enabledActions, routeSetup } from './setup-words'
import { AlertPlainPage, AlertWorkspace, ArSection, SetupNotice, SetupPill, TypeLogo } from './workspace-parts'
import './alert-routing.scss'

export { toBody }

// The editor's sections (SectionNav), once tabs: ?step=N (the routes list's
// "test") scrolls to the Nth.
export const ROUTE_SECTIONS = [
  { id: 'trigger', label: 'Trigger' },
  { id: 'filters', label: 'Filters' },
  { id: 'when', label: 'When' },
  { id: 'targets', label: 'Targets' },
  { id: 'test', label: 'Test' }
]

// What stops a save, by the section it is about. "Only these alerts" with
// nothing ticked is never read as "every alert": the server would take empty
// triggerKinds as the wildcard. The server refuses a route with no target (its
// words: "Add at least one action").
export const routeProblems = (r, catalog) => {
  const out = []
  if (r.choosing && r.triggerKinds.length === 0) out.push({ section: 'trigger', text: `Tick at least one alert, or choose every ${categoryNoun(r.category, catalog)} alert.` })
  if (!(r.actions || []).some(a => a.targetID)) out.push({ section: 'targets', text: 'Add at least one target.' })
  return out
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const dayWords = days => {
  if (!days || days.length === 0 || days.length === 7) return 'Every day'
  const set = [...days].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b)).join(',')
  if (set === 'mon,tue,wed,thu,fri') return 'Weekdays'
  if (set === 'sat,sun') return 'Weekends'
  return `${days.length} days a week`
}

// The summary strip (status-strip.js tiles), from the route as edited: its
// alerts, filters, when, targets, and when it last matched (saved).
export const routeTiles = (route, catalog, targets, lastMatchedAt) => {
  const category = (catalog.categories.find(c => c.key === route.category) || {}).label || route.category
  const kinds = route.choosing ? route.triggerKinds.length : 0
  const on = enabledActions(route)
  const names = on.map(a => (targets.find(t => t.id === a.targetID) || {}).name).filter(Boolean)
  const s = route.schedule
  return [
    { key: 'alerts', label: 'alerts', value: route.choosing ? plural(kinds, 'alert') : 'All alerts', detail: category, section: 'trigger' },
    {
      key: 'filters', label: 'filters', value: route.filters.length ? plural(route.filters.length, 'filter') : 'None', section: 'filters',
      detail: (SEVERITY_FLOOR[route.minSeverity] || '').toLowerCase()
    },
    {
      key: 'when', label: 'when', value: s ? `${dayWords(s.days)} ${s.from}–${s.to}` : 'Any time', section: 'when',
      detail: route.deliveryMode === 'digest' ? `sent in ${digestEvery(route.digestMinutes)}` : 'sent at once'
    },
    {
      key: 'targets', label: 'targets', value: String(on.length), section: 'targets', tone: on.length ? null : 'warn', dot: on.length ? 'ok' : 'warn',
      detail: on.length ? (names.length > 1 ? `${names[0]} and ${names.length - 1} more` : names[0] || plural(on.length, 'target')) : 'sends nowhere yet'
    },
    {
      key: 'matched', label: 'last matched', value: lastMatchedAt ? formatAgo(lastMatchedAt) : 'Never', section: 'test',
      detail: lastMatchedAt ? formatDateTime(lastMatchedAt) : 'no alert has matched yet'
    }
  ]
}

// A route's state as its header pill.
export const routePill = r => (!r.enabled ? { label: 'Off', tone: null } : r.paused ? { label: 'Paused', tone: 'warn', reason: 'Held until resumed' } : { label: 'On', tone: 'ok' })

// /admin/alert-routing/routes/:routeID (and /new): a route in the SSO
// connection workspace's anatomy (workspace-parts.js), its old tabs as
// sections with the section nav (Trigger, Filters, When, Targets, Test). The
// header: the route's icon, name, category and scope, its state, Save and a ⋮
// (turn on or off, pause or resume, delete last in red). Saving is the docked
// save bar. A route with no target switched on is "Setup incomplete", with
// Add a target. A new route from a target's page (?target=) starts with that
// target.
export const RouteEditor = () => {
  const { routeID } = useParams()
  const { search } = useLocation()
  const history = useHistory()
  const params = new URLSearchParams(search)
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const isNew = routeID === 'new'
  const [route, setRoute] = useState(() => {
    if (!isNew) return null
    const blank = emptyRoute(params.get('category'))
    const target = Number(params.get('target'))
    return target ? { ...blank, actions: [{ targetID: target, enabled: true, mapping: {} }] } : blank
  })
  const [saved, setSaved] = useState(null)
  // what the editor doesn't edit: on or off, paused, when it last matched
  const [meta, setMeta] = useState(null)
  const [targets, setTargets] = useState([])
  const [error, failure, setError] = useErrorLine()
  const [said, setSaid] = useState('')
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, deleteFailure, setDeleteError] = useErrorLine()
  const [actionError, actionFailure, setActionError] = useErrorLine()
  // The id a create just returned: the URL moves to it, and the route in
  // hand is already what the server stored, so it is not fetched again.
  const created = useRef(null)
  const step = Number(params.get('step') || 0)

  useEffect(() => {
    if (isNew || created.current === routeID) return
    getRoute(routeID).then(r => { const v = editableRoute(r); setRoute(v); setSaved(v); setMeta(r) },
      err => setError(serverMessage(err, "This route couldn't be loaded."), err))
  }, [routeID, isNew, setError])

  // the targets' names, for the strip
  useEffect(() => {
    let live = true
    Promise.resolve(listTargets()).then(v => live && setTargets(v || []), () => {})
    return () => { live = false }
  }, [])

  // ?step=N: the routes list's "test" opens at the Test section.
  const ready = Boolean(route)
  useEffect(() => {
    if (ready && step >= 1 && step <= ROUTE_SECTIONS.length) scrollToSection(ROUTE_SECTIONS[step - 1].id)
  }, [ready]) // eslint-disable-line react-hooks/exhaustive-deps

  // History: whose route this is, once loaded (null: a platform route), for
  // a reader without SYS_AUDIT_LOG (record-routes.js recordOrg)
  useAuditRecord({ type: 'alert_route', id: !isNew && saved ? routeID : undefined, organizationID: !isNew && saved ? (saved.organizationID ?? null) : undefined })

  const body = useMemo(() => (route ? toBody(route) : null), [route])
  const dirty = !saved || JSON.stringify(toBody(saved)) !== JSON.stringify(body)
  // A refused save belongs to the edit it was about: the next edit clears it.
  const bodyKey = JSON.stringify(body)
  useEffect(() => { setError(null) }, [bodyKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = patch => setRoute(r => ({ ...r, ...patch }))
  const problems = route ? routeProblems(route, catalog) : []

  if (isNew && !canEdit) {
    return (
      <AlertPlainPage list="routes" crumb="New route" title="New alert route" logo={<TypeLogo Icon={IconAlertRoute} />}>
        <Notice tone="warn">{reason}</Notice>
      </AlertPlainPage>
    )
  }
  if (!route) {
    return (
      <AlertPlainPage list="routes" crumb="Route" title={error ? 'Route not found' : 'Loading…'} logo={<TypeLogo Icon={IconAlertRoute} />}>
        {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      </AlertPlainPage>
    )
  }

  const readOnly = !canEdit
  // A generated secret not yet confirmed holds the editor: no save, no leaving.
  const held = pendingSecret(route)
  const canSave = canEdit && !held && Boolean(route.name.trim()) && (isNew || dirty)

  const save = async () => {
    if (!canSave || busy) return
    if (problems.length) { setError(problems[0].text); return }
    setBusy(true)
    try {
      setError(null)
      setSaid('')
      const out = isNew ? await createRoute(body) : await updateRoute(routeID, body)
      setSaved({ ...route })
      setMeta(m => ({ ...(m || {}), ...(out || {}) }))
      setSaid(isNew ? 'Created.' : 'Saved.')
      if (isNew) {
        created.current = String(out.id)
        history.replace(routeHref(out.id))
      }
    } catch (err) {
      setError(serverMessage(err, "The route couldn't be saved."), err)
    } finally {
      setBusy(false)
    }
  }
  const discard = () => {
    if (isNew) { history.push(ROUTES_PAGE); return }
    setRoute(saved)
    setError(null)
  }
  const changeState = async action => {
    try {
      setActionError(null)
      await routeState(routeID, action)
      setMeta(await getRoute(routeID))
    } catch (err) {
      setActionError(serverMessage(err, "That didn't work."), err)
    }
  }
  const remove = async () => {
    setDeleteError(null)
    try {
      await deleteRoute(routeID)
      history.push(ROUTES_PAGE)
    } catch (err) {
      setDeleteError(serverMessage(err, "The route couldn't be deleted."), err)
    }
  }
  const addTarget = () => {
    set({ actions: [...route.actions, { targetID: 0, enabled: true, mapping: {} }] })
    scrollToSection('targets')
  }

  // Read-only: every section's inputs are disabled; Test stays usable for
  // Preview (which never writes) and offers no Send test.
  const stepProps = { route, set, body, savedID: isNew ? null : Number(routeID), dirty, readOnly, problems: problems.filter(p => p.section === 'trigger').map(p => p.text) }
  const fenced = Step => (readOnly
    ? <fieldset disabled className="ar-readonly"><Step {...stepProps} /></fieldset>
    : <Step {...stepProps} />)
  const nameField =
    <div className="sw-field ar-narrow mb-3">
      <label className="sw-label ar-required" htmlFor="ar-name">Route name</label>
      <input id="ar-name" className="form-control" maxLength={120} value={route.name} readOnly={readOnly} onChange={e => set({ name: e.target.value })} />
    </div>
  const sections =
    <>
      <ArSection id="trigger" title="Trigger" lede="Which alerts this route picks up.">{nameField}{fenced(TriggerStep)}</ArSection>
      <ArSection id="filters" title="Filters" lede="Only alerts that match go on to its targets.">{fenced(FiltersStep)}</ArSection>
      <ArSection id="when" title="When" lede="When it sends, and whether alerts wait to go out together.">{fenced(WhenStep)}</ArSection>
      <ArSection id="targets" title="Targets" lede="Where its alerts go. Each can be switched off here."
        actions={canEdit && <button type="button" className="btn sw-btn btn-sm" disabled={held} onClick={addTarget}><IconAdd /> Add a target</button>}>
        {fenced(ActionsStep)}
      </ArSection>
      <ArSection id="test" title="Test" lede="Preview what it would send, or send a test once it's saved."><TestStep {...stepProps} /></ArSection>
    </>
  const category = (catalog.categories.find(c => c.key === route.category) || {}).label || route.category
  const scope = routeOrgScoped(route) ? route.organizationName || `Organization ${route.organizationID}` : 'Platform'
  const setup = !isNew && saved ? routeSetup(saved) : null
  const notices =
    <div className="ar-notices">
      {readOnly && <Notice tone="warn">{reason}</Notice>}
      {said && !dirty && <Notice tone="ok" role="status">{said}</Notice>}
      {held && <Notice tone="warn">Copy the new target's secret and choose "I've copied it" before you go on.</Notice>}
      {actionError && <Notice tone="bad" role="alert">{actionError}<ErrorReference error={actionFailure} compact /></Notice>}
      {error && readOnly && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {setup && <SetupNotice setup={setup} onAction={canEdit ? addTarget : null} disabled={held} />}
    </div>
  const saveBar = canEdit &&
    <DockedSaveBar testId="route-savebar" on={isNew || dirty} count={0} error={error} failure={failure} busy={busy} canSave={canSave}
      status={<span className="sw-unsaved-count" role="status">{isNew ? "It's created when you save." : 'Unsaved changes'}</span>}
      onSave={save} onDiscard={discard} saveLabel={isNew ? 'Create route' : 'Save'} busyLabel={isNew ? 'Creating…' : 'Saving…'} discardLabel={isNew ? 'Cancel' : 'Discard'} />
  const guards =
    <>
      <LeaveGuard when={held} title="Copy the secret first" label="Copy the secret first" leaveLabel="Leave and lose the secret"
        message="The new target's secret is shown only once. If you leave now, it can't be shown again and the target will need a new secret." />
      <LeaveGuard when={!held && !isNew && dirty} message="Leave this route without saving them?" />
    </>

  if (isNew) {
    return (
      <AlertWorkspace list="routes" crumb="New route" title="New alert route" subtitle={`${category} · ${scope}`} logo={<TypeLogo Icon={IconAlertRoute} />}
        tiles={routeTiles(route, catalog, targets, null)} sections={ROUTE_SECTIONS}>
        {guards}
        {notices}
        {sections}
        {saveBar}
      </AlertWorkspace>
    )
  }

  const state = routePill(meta || saved)
  const headerActions = canEdit &&
    <>
      <button type="button" className="btn sw-btn-primary" disabled={!canSave || busy} onClick={save}>Save</button>
      <Menu sentenceCase forceDropdown>
        <MenuItem text={(meta || saved).enabled ? 'Turn off' : 'Turn on'} pin={false} show onClick={() => changeState((meta || saved).enabled ? 'disable' : 'enable')} />
        <MenuItem text={(meta || saved).paused ? 'Resume' : 'Pause'} pin={false} show={Boolean((meta || saved).enabled)} onClick={() => changeState((meta || saved).paused ? 'resume' : 'pause')} />
        <MenuItem Icon={IconDelete} text="Delete" danger pin={false} show onClick={() => { setDeleteError(null); setDeleting(true) }} />
      </Menu>
    </>

  return (
    <AlertWorkspace list="routes" crumb={saved.name || 'Route'}
      title={saved.name || 'Route'} subtitle={`${category} · ${scope}`} logo={<TypeLogo Icon={IconAlertRoute} />}
      status={state} badges={setup ? <SetupPill /> : null} headerActions={headerActions}
      tiles={routeTiles(route, catalog, targets, meta && meta.lastMatchedAt)} sections={ROUTE_SECTIONS}>
      {guards}
      {notices}
      {sections}
      {saveBar}
      <ConfirmDialog open={deleting} scrim title={`Delete the route "${saved.name}"?`} confirmText="Delete route"
        body="Alerts it would have sent stop now. This can't be undone." error={deleteError} failure={deleteFailure}
        onCancel={() => setDeleting(false)} onConfirm={remove} />
    </AlertWorkspace>
  )
}
